// Shared triage read-out: renders a triage assessment (agentic or trainee).
// Extracted from the former TriagePanel so both the agentic and human panels
// in TriageTraining render the same inspector.

export const COMPLEXITY_STYLE = {
  low: 'badge-success',
  low_moderate: 'badge-success',
  moderate: 'badge-warning',
  moderate_high: 'badge-warning',
  high: 'badge-error',
  highest: 'badge-error',
}

export function complexityLabel(tier) {
  return (tier || '').replace(/_/g, '-') || '—'
}

export function confidenceColor(c) {
  if (c == null) return 'bg-base-300'
  if (c >= 0.8) return 'bg-success'
  if (c >= 0.5) return 'bg-warning'
  return 'bg-error'
}

function DifferentialBar({ item, buckets }) {
  const label = buckets[item.bucket_id]?.label || item.bucket_id
  const pct = Math.round((item.confidence || 0) * 100)
  return (
    <div className="text-xs">
      <div className="flex justify-between gap-2">
        <span className="truncate font-medium">{label}</span>
        <span className="font-mono text-base-content/60">{item.confidence?.toFixed(2)}</span>
      </div>
      <div className="h-1.5 bg-base-200 rounded mt-0.5 overflow-hidden">
        <div className={`h-full ${confidenceColor(item.confidence)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// `assessment` is a TriageSession.state_snapshot() shape; `handoff` is optional.
export default function TriageReadout({ assessment, buckets = {}, handoff, empty }) {
  if (!assessment) {
    return (
      <div className="text-sm text-base-content/60">
        {empty || 'The triage read-out appears here: suspected category, confidence, differential, missing data, and grounded evidence.'}
      </div>
    )
  }
  const a = assessment
  const bucketLabel = buckets[a.suspected_bucket]?.label || a.suspected_bucket
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Suspected category</div>
        <div className="inline-block bg-primary text-primary-content rounded-lg px-3 py-1.5 text-sm font-semibold break-words max-w-full">
          {bucketLabel}
        </div>
        {a.complexity_tier && (
          <div className="mt-1.5">
            <span className={`badge ${COMPLEXITY_STYLE[a.complexity_tier] || 'badge-ghost'} badge-outline`}
              title="Disorder complexity (from taxonomy)">
              {complexityLabel(a.complexity_tier)} complexity
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 h-2 bg-base-200 rounded overflow-hidden">
            <div className={`h-full ${confidenceColor(a.confidence)}`} style={{ width: `${Math.round((a.confidence || 0) * 100)}%` }} />
          </div>
          <span className="font-mono text-xs">{a.confidence?.toFixed?.(2) ?? '—'}</span>
        </div>
      </div>

      {a.differential?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-base-content/60 mb-2">Differential</div>
          <div className="space-y-2">
            {a.differential.map((d) => <DifferentialBar key={d.bucket_id} item={d} buckets={buckets} />)}
          </div>
        </div>
      )}

      {a.comorbidity_screening?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Comorbidities to screen</div>
          <div className="flex flex-wrap gap-1">
            {a.comorbidity_screening.map((c, i) => <span key={i} className="badge badge-outline badge-sm">{c}</span>)}
          </div>
        </div>
      )}

      {a.missing_information?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Still missing</div>
          <ul className="list-disc list-inside text-sm space-y-0.5 text-base-content/80">
            {a.missing_information.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {a.citations_used?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Grounded in</div>
          <div className="flex flex-wrap gap-1">
            {a.citations_used.map((c) => <span key={c} className="badge badge-ghost badge-sm font-mono">{c}</span>)}
          </div>
        </div>
      )}

      {handoff?.recommended_data_collection?.length > 0 && (
        <div className="border-t border-base-200 pt-3">
          <div className="text-xs uppercase tracking-wide text-base-content/60 mb-1">Recommended data collection</div>
          <ul className="list-disc list-inside text-sm space-y-0.5">
            {handoff.recommended_data_collection.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}
